/**
 * summaryItems — registry + builders for the ProductView SUMMARY pane DataGrid.
 *
 * ## How to add a new item
 *
 * 1. Add an ID to `SUMMARY_ITEM_ID` in `constants/summaryItemIds.js`.
 * 2. Add a settings flag in `buildSummarySettings()` below.
 * 3. Add one entry to `SUMMARY_ITEM_REGISTRY`.
 *
 * No changes needed anywhere else — the component reads all items from the
 * registry automatically.
 *
 * UI-free, JSX-free, fully testable.
 */

import { SUMMARY_ITEM_ID } from '../constants/summaryItemIds.js'

import {
  formatProductArea,
  formatTier,
  formatCompartmentInsolation,
  formatCompartmentInsolationRange,
} from '../../../shared/utils/productFormatters.js'

// ── Internal helpers ──────────────────────────────────────────────────────────

/** * Returns the desired column span (1, 2, or 3) for a value string.
 * Used for fields that may have variable-length content (exposition, outdoor areas).
 * DataGrid will further clamp to ‘full’ when span ≥ the actual column count.
 */
function autoSpan(value) {
  const len = String(value ?? '').length
  return len > 28 ? 3 : len > 13 ? 2 : 1
}

/** * Returns a comma-separated list of translated outdoor compartment names
 * that appear in the product's compartments array.
 * Deduplicates by name_cad. Returns null if none found.
 *
 * @param {object}   product
 * @param {object|null} compartmentTypes — full compartments.json object or `compartments_types` map
 * @param {function} t
 * @returns {string|null}
 */
function formatOutdoorCompartmentList(product, compartmentTypes, t) {
  const types = compartmentTypes?.compartments_types ?? compartmentTypes ?? {}
  const compartments = Array.isArray(product?.compartments) ? product.compartments : []

  const seen = new Set()
  const names = []

  for (const compartment of compartments) {
    const nameCad = compartment?.name_cad
    if (!nameCad || seen.has(nameCad)) continue
    if (types[nameCad]?.group !== 'OutdoorSpace') continue
    seen.add(nameCad)
    names.push(t(`compartments.${nameCad}`, nameCad))
  }

  return names.length > 0 ? names.join(', ') : null
}

// ── Registry ──────────────────────────────────────────────────────────────────

/**
 * SUMMARY_ITEM_REGISTRY — declarative list of all possible SUMMARY pane data items.
 *
 * Each entry:
 *   id           — matches SUMMARY_ITEM_ID
 *   labelKey     — i18n key for the DataItem label
 *   isAvailable  — (settings) => boolean — capability/settings gate
 *   getValue     — (product, t, context) => string|null — extracts + formats the value;
 *                  returning null silently drops the item from the rendered grid
 *   getTooltip   — (product, t, context) => string|null — optional; if present and non-null,
 *                  a (?) icon is rendered next to the label via DataItem's tooltip prop
 *
 * Registry order determines render order.
 *
 * context shape: { compartmentTypes: object|null, panelSettings: object|null }
 */
  //// INVESTMENT:      'investment',
  //// STAGE:      'stage',
  //// LOT_AREA`:        'lot_area',
  //// AREA_PERCENTILE: 'area_percentile',
  //// HEIGHT:        'height',
  //// SPECIAL_AREAS: 'special_areas',
  //// ENERGY_CLASS:   'energy_class',
export const SUMMARY_ITEM_REGISTRY = [
  {
    id: SUMMARY_ITEM_ID.INVESTMENT,
    labelKey: 'product.investment',
    isAvailable: (settings) => settings.showInvestment,
    getValue: (product) => product?.investment_name ?? null,
    getSpan: (value) => autoSpan(value),
  },
  {
    id: SUMMARY_ITEM_ID.STAGE,
    labelKey: 'product.stage',
    isAvailable: (settings) => settings.showStage,
    getValue: (product) => product?.stage_name ?? null,
    getSpan: (value) => autoSpan(value),
  },
  {
    id: SUMMARY_ITEM_ID.BUILDING,
    labelKey: 'product.building',
    isAvailable: (settings) => settings.showBuilding,
    getValue: (product) => product?.building_name ?? null,
    getSpan: (value) => autoSpan(value),
  },
  {
    id: SUMMARY_ITEM_ID.GATE,
    labelKey: 'product.gate',
    isAvailable: (settings) => settings.showGate,
    getValue: (product) => {
      const gate = product?.gate
      return gate != null && gate !== '' ? String(gate) : null
    },
  },
  {
    id: SUMMARY_ITEM_ID.FLOOR,
    labelKey: 'product.floor',
    isAvailable: (settings) => settings.showFloor,
    getValue: (product, _t, { tiers }) => formatTier(product, tiers),
  },
  {
    id: SUMMARY_ITEM_ID.ROOMS,
    labelKey: 'product.rooms',
    isAvailable: (settings) => settings.showRooms,
    getValue: (product) => {
      const rooms = product?.rooms
      return rooms != null ? String(rooms) : null
    },
  },
  {
    id: SUMMARY_ITEM_ID.AREA,
    labelKey: 'product.area_m2',
    isAvailable: (settings) => settings.showArea,
    getValue: (product) => formatProductArea(product),
  },
  {
    id: SUMMARY_ITEM_ID.AREA_PERCENTILE,
    labelKey: 'product.area_percentile',
    isAvailable: (settings) => settings.showAreaPercentile,
    getValue: (product) => formatProductAreaPercentile(product),
  },
  // {
    // id: SUMMARY_ITEM_ID.LOT_AREA,
    // labelKey: 'product.lot_area',
    // isAvailable: (settings) => settings.showLotArea,
    // getValue: (product) => formatProductLotArea(product),
  // },
  {
    id: SUMMARY_ITEM_ID.EXPOSITION,
    labelKey: 'product.exposition',
    isAvailable: (settings) => settings.showExposition,
    getValue: (product, t) => {
      const exp = product?.exposition
      if (!Array.isArray(exp) || exp.length === 0) return null
      return exp.map((d) => t(`directions.compass_direction_${d.toLowerCase()}`, d)).join(', ')
    },
    getTooltip: (_product, t) => t('product.tooltip_exposition') || null,
    getSpan: (value) => autoSpan(value),
  },
  {
    id: SUMMARY_ITEM_ID.OUTDOOR_AREAS,
    labelKey: 'product.outdoor_areas',
    isAvailable: (settings) => settings.showOutdoorAreas,
    getValue: (product, t, { compartmentTypes }) =>
      formatOutdoorCompartmentList(product, compartmentTypes, t),
    getSpan: (value) => autoSpan(value),
  },
  {
    id: SUMMARY_ITEM_ID.SPECIAL_AREAS,
    labelKey: 'product.special_areas',
    isAvailable: (settings) => settings.showSpecialAreas,
    getValue: (product, t, { compartmentTypes }) =>
      formatOutdoorCompartmentList(product, compartmentTypes, t),
    getSpan: (value) => autoSpan(value),
  },
  {
    id: SUMMARY_ITEM_ID.INSOLATION_HOURS,
    labelKey: 'product.insolation_hours',
    isAvailable: (settings) => settings.showInsolationHours,
    getValue: (product) => formatCompartmentInsolation(product),
    getTooltip: (_product, t) => t('product.tooltip_insolation') || null,
  },
  {
    id: SUMMARY_ITEM_ID.INSOLATION_RANGE,
    labelKey: 'product.insolation_range',
    isAvailable: (settings) => settings.showInsolationRange,
    getValue: (product) => formatCompartmentInsolationRange(product),
    getTooltip: (_product, t) => t('product.tooltip_insolation') || null,
    getSpan: (value) => autoSpan(value),
  },
  {
    id: SUMMARY_ITEM_ID.TURNKEY_STYLE,
    labelKey: 'product.turn_key_style',
    isAvailable: (settings) => settings.showTurnkeyStyle,
    getValue: (product, _t, { panelSettings }) => {
      const styles = panelSettings?.turn_key_styles?.styles ?? []
      const match = styles.find((s) => s.label === product?.turn_key_style)
      return match?.label ?? null
    },
    getTooltip: (product, _t, { panelSettings }) => {
      const styles = panelSettings?.turn_key_styles?.styles ?? []
      const match = styles.find((s) => s.label === product?.turn_key_style)
      return match?.tooltip?.text || null
    },
    getSpan: (value) => autoSpan(value),
  },
]

// ── Settings builder ──────────────────────────────────────────────────────────

/**
 * buildSummarySettings — derives display flags for the SUMMARY pane from
 * real panelSettings keys + capabilities + buildings.
 *
 * CMS overrides live under `panelSettings.unit_sheet_summary_pane`.
 * All per-field flags default to `true` when the key is absent.
 * Fields with a broader gate (capability or project config) are additionally
 * gated here regardless of the per-field toggle.
 *
 * @param {object|null} panelSettings
 * @param {object}      capabilities — from ProductContext
 * @param {object[]}    buildings    — from CatalogContext
 * @returns {object}
 */
export function buildSummarySettings(panelSettings, capabilities, buildings) {
  const cfg = panelSettings?.unit_sheet_summary_pane ?? {}
  const isMultiBuilding = Array.isArray(buildings) && buildings.length > 1

  return {
    showStatus:                              (cfg.status         ?? true),
    showBuilding:     isMultiBuilding     && (cfg.building     ?? true),
    showGate:                                (cfg.gate         ?? true),
    showFloor:                               (cfg.floor        ?? true),
    showRooms:                               (cfg.rooms        ?? true),
    showArea:                                (cfg.area         ?? true),
    showExposition:                          (cfg.exposition   ?? true),
    showOutdoorAreas:                        (cfg.outdoor_areas ?? true),
    showInsolationHours:   !!(capabilities?.hasInsolationCommissioned) && (cfg.insolation_hours   ?? true),
    showInsolationRange:   !!(capabilities?.hasInsolationCommissioned) && (cfg.insolation_range   ?? true),
    showTurnkeyStyle: !!(panelSettings?.turn_key_styles?.styles?.some(s => s.in_use)) && (cfg.turn_key_style ?? true),
  }
}

// ── Main builder ──────────────────────────────────────────────────────────────

/**
 * buildSummaryItems — builds the DataGrid items array for the SUMMARY pane.
 *
 * Iterates SUMMARY_ITEM_REGISTRY, filters by settings flags, and maps each
 * entry to a `{ label, value, tooltip? }` object. Entries whose `getValue`
 * returns null are silently dropped.
 *
 * @param {object|null} product
 * @param {object}      settings  — from buildSummarySettings()
 * @param {function}    t         — translation function from useI18n
 * @param {object}      context
 * @param {object|null} context.compartmentTypes — full compartments.json or compartments_types map
 * @param {object|null} context.panelSettings    — passed through for dynamic tooltip/value lookups
 * @param {object|null} context.tiers            — passed through for tier names lookups
 * @returns {Array<{label: string, value: string, tooltip?: string}>}
 */
export function buildSummaryItems(product, settings, t, context) {
  if (!product) return []

  const items = []

  for (const entry of SUMMARY_ITEM_REGISTRY) {
    if (!entry.isAvailable(settings)) continue
    const value = entry.getValue(product, t, context)
    if (value == null) continue
    const tooltip = entry.getTooltip ? entry.getTooltip(product, t, context) : undefined
    const span    = entry.getSpan    ? entry.getSpan(value)                   : undefined
    items.push({
      label: t(entry.labelKey),
      value,
      ...(tooltip != null ? { tooltip } : {}),
      ...(span    != null ? { span }    : {}),
    })
  }

  return items
}
